
import { AsyncPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject,
    input
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputComponent } from "@components/input/input.component";
import { PillboxComponent } from "@components/pillbox/pillbox.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import {
    Observable,
    Subject,
    Subscription,
    asapScheduler,
    combineLatest,
    firstValueFrom,
    of
} from "rxjs";
import { catchError, map, takeUntil, withLatestFrom } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import {
    ConfirmDialogResponse,
    ErsattningsgrundandeMatning,
    RaskRoller,
    VirkeskopAffarsformer,
    VirkeskopKontraktstatus
} from "~app/common/models/constants";
import { IVirkeskopKontraktEntityDTO } from "~app/common/models/entity-models";
import { Period } from "~app/common/models/period";
import { RuleValidationMessage } from "~app/common/services/cqs/models/rule-validation-message";
import { FeatureService } from "~app/common/services/feature.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Kontrakt, VirkeskopKontraktstyp, VirkeskopNettotyper } from "~app/features/virkeskop/models/kontrakt-models";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { canEdit, updatePermissions } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../common/directives/disabled-control/disabled-control.directive";
import * as VirkeskopFieldLengthConstants from "../../constants/virkeskop-field-length-constants";
import { IVKKopplingDTO } from "../../dto/kontrakt/kontrakt-kopplingar-dto";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { KontraktStatusService } from "../../services/kontrakt-status.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VirkeskopValidationRuleDirective } from "../../validation/directives/virkeskop-validation-rule.directive";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
import { KontraktHeaderIconsComponent } from "./kontrakt-header-icons/kontrakt-header-icons.component";
import { LeverantorsOrganisationerPopoverComponent } from "./leverantors-organisationer-popover/leverantors-organisationer-popover.component";
import { VacsStatusComponent } from "./vacs-status/vacs-status.component";
import { ViolEditorPopoverComponent } from "./viol-editor-popover/viol-editor-popover.component";
@Component({
    selector: "rask-kontrakt-header",
    templateUrl: "./kontrakt-header.component.html",
    styleUrls: ["./kontrakt-header.component.scss"],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        VacsStatusComponent,
        PillboxComponent,
        NgClass,
        IconComponent,
        ViolEditorPopoverComponent,
        ActionButtonComponent,
        LeverantorsOrganisationerPopoverComponent,
        KontraktHeaderIconsComponent,
        VirkeskopValidationMessageComponent,
        InputComponent,
        VirkeskopValidationRuleDirective,
        DisabledControlDirective,
        SimpleSelectorComponent,
        SelectorComponent,
        DatepickerComponent,
        CheckboxComponent,
        AsyncPipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        updatePermissions({
            role: RaskRoller.role.Kopplanera,
            canEdit: false
        })
    ]
})

export class KontraktHeaderComponent implements OnInit, OnDestroy {

    readonly entity = input<IVirkeskopKontraktEntityDTO>();
    form: UntypedFormGroup;
    avtalDirty: boolean;
    angeSkordarmatningIsActive = false;
    protected visaSkordarmatningIsActive$: Observable<boolean>;
    protected kontraktstypInfotext = "Kontraktstyp kan inte ändras när avtalet är osparat";
    protected externBeteckningInfotext = "Referens hos kund/leverantör";
    protected isSkogsvardskontrakt: boolean;
    protected isSkogsvardFPVKontrakt: boolean;

    protected kontrakt: Kontrakt = new Kontrakt();

    protected affarsformDisabled$: Observable<boolean>;
    protected kontraktTypDisabled$: Observable<boolean>;
    protected kontraktStatusStates$: Observable<string[]>;
    protected isReadonly$: Observable<boolean>;
    protected kontraktBenamningMaxLength = VirkeskopFieldLengthConstants.KontraktBenamningMaxLength;

    protected affarsformerItems = VirkeskopAffarsformer.AllaWithShortType
        .map(({ key, value }) => ({
            key: `${key} - ${value}`,
            value
        }));
    protected kontrakttyperItems: string[] = [
        VirkeskopKontraktstyp.Rundvirke,
        VirkeskopKontraktstyp.Grot
    ];

    protected isGrotuttagForeslaget: boolean;
    protected kopplingar: IVKKopplingDTO[];

    protected nettotyperItems = [
        {
            text: VirkeskopNettotyper.Sortimentsnetto,
            value: VirkeskopNettotyper.Sortimentsnetto
        },
        {
            text: VirkeskopNettotyper.Snittnetto,
            value: VirkeskopNettotyper.Snittnetto
        }
    ];
    protected matningTypSelectionList = [
        {
            text: ErsattningsgrundandeMatning.Skordarmatning,
            value: true
        },
        {
            text: ErsattningsgrundandeMatning.Industrimatning,
            value: false
        }
    ];
    private formSubscription: Subscription;
    private readonly destroy$ = new Subject<void>();

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly kontraktStatusService = inject(KontraktStatusService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly featureService = inject(FeatureService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly canEdit = canEdit();

    constructor() {

        this.kontraktStatusStates$ = this.kontraktStatusService.kontraktStatusStates$;

        this.kontraktMediatorService.kontrakt$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(kontrakt => {
            this.kontrakt = kontrakt;

            this.initKontraktForm();
        });

        this.kontraktMediatorService.atgarder$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(atgarder => {
            this.isGrotuttagForeslaget = atgarder.some(x => x.grotuttagForeslaget === true);
        });

        this.kontraktMediatorService.kontraktKopplingar$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(kopplingar => {
            this.kopplingar = kopplingar?.kopplingar?.reduce((agg, item) => {
                if (!agg.some(x => x.koppladVKKontraktId === item.koppladVKKontraktId) && item.koppladVKKontraktId !== this.kontrakt.id) {
                    agg.push(item);
                }
                return agg;
            }, [] as IVKKopplingDTO[]);
        });

        this.avtalMediatorService.dirty$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(dirty => {
            this.avtalDirty = dirty;
        });

        // eslint-disable-next-line sonarjs/no-async-constructor
        firstValueFrom(this.featureService.getFeatureActive("AngeSkordarmatningVirkeskopskontrakt")).then(x => {
            this.angeSkordarmatningIsActive = x;
        });
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.visaSkordarmatningIsActive$ = combineLatest([
            this.featureService.getFeatureActive("VisaSkordarmatningVirkeskopskontrakt"),
            this.kontraktMediatorService.kontrakt$
        ]).pipe(
            map(([featureActive, kontrakt]) => featureActive && (kontrakt.isAffarsformU4 || kontrakt.isAffarsformU5 || kontrakt.isAffarsformU6))
        );

        this.affarsformDisabled$ = this.avtalMediatorService.dirty$.pipe(
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([
                dirty,
                kontrakt
            ]) => {
                if (ObjectUtils.isNullOrUndefined(kontrakt.affarsform)) {
                    return false;
                }
                return dirty || !!kontrakt.skickadTillVacs || kontrakt.hasArbetsorderWhereProduktionsplaneringPagar;
            })
        );
        this.kontraktTypDisabled$ = this.avtalMediatorService.dirty$;
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
        this.isSkogsvardskontrakt = this.kontrakt.isSkogsvardsKontrakt;
        this.isSkogsvardFPVKontrakt = this.kontrakt.isSkogsvardFPVKontrakt;
        if (this.isSkogsvardskontrakt) {
            this.kontraktstypInfotext = "";
        }
    }

    beforeAffarsformChange = () => {
        if (!this.kontrakt.affarsform) {
            return of(true);
        }
        const settings = new ModalSettings("Förändring av affärsform",
            "Vid byte av affärsform kan information gå förlorad som ej går att återställa, är du säker på att du vill byta affärsform?");
        settings.yesCaption = "Bekräfta byte av affärsform";
        settings.noCaption = "Avbryt";
        const confirm = this.raskModalService.confirm(settings);

        return confirm
            .pipe(map(x => x === ConfirmDialogResponse.save ? true : false))
            .pipe(catchError(() => of(false)));
    };

    affarsformChanged(affarsform: string) {
        if (affarsform === this.kontrakt.affarsform) {
            return;
        }

        const shouldSendChangeCommand = !ObjectUtils.isNullOrUndefined(this.kontrakt.affarsform);
        this.form.controls.affarsform.setValue(affarsform);
        this.statusChanged();

        if (shouldSendChangeCommand) {
            this.kontraktMediatorService.updateKontraktAffarsform(this.kontrakt);
        } else {
            this.kontraktMediatorService.handleKontraktAffarsformChange(affarsform);
            this.onHeaderUpdated();
        }
    }

    beforeKontraktTypChange = () => {
        if (!this.kontrakt.kontraktTyp) {
            return of(true);
        }
        const settings = new ModalSettings("Förändring av kontraktstyp",
            "Vid byte av kontraktstyp kan information gå förlorad som ej går att återställa, är du säker på att du vill byta kontraktstyp?");
        settings.yesCaption = "Bekräfta byte av kontraktstyp";
        settings.noCaption = "Avbryt";
        const confirm = this.raskModalService.confirm(settings);

        return confirm
            .pipe(map(x => x === ConfirmDialogResponse.save ? true : false))
            .pipe(catchError(() => of(false)));
    };

    typChanged(kontraktTyp: string) {
        if (kontraktTyp === this.kontrakt.kontraktTyp) {
            return;
        }

        this.kontrakt.kontraktTyp = kontraktTyp;
        if (this.kontrakt.id) {
            this.kontraktMediatorService.updateKontraktTyp(this.kontrakt);
        } else {
            this.onHeaderUpdated();
        }
    }

    statusChanged() {
        if (this.kontrakt.status !== VirkeskopKontraktstatus.Kontrakt) {
            this.form.controls.anbudGiltighetFrom.setValue(undefined);
        }
        this.form.controls.anbudGiltighetTom.setValue(undefined);
        this.form.controls.kontraktGiltigtFrom.setValue(undefined);
        this.form.controls.kontraktGiltigtTom.setValue(undefined);

        if (this.kontrakt.status === VirkeskopKontraktstatus.Anbud) {
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            const giltigtFrom = date.toLocaleString("sv");
            date.setMonth(date.getMonth() + 1);
            const giltigtTom = date.toLocaleString("sv");

            this.form.controls.anbudGiltighetFrom.setValue(giltigtFrom);
            this.form.controls.anbudGiltighetTom.setValue(giltigtTom);
            this.setAnbudGiltighetChanged();
        } else if (this.kontrakt.status === VirkeskopKontraktstatus.Kontrakt) {
            let giltigtFrom = this.kontrakt.anbudGiltighetFrom;
            if (!giltigtFrom || this.kontrakt.affarsform === VirkeskopAffarsformer.Leveransvirke) {
                const newDate = new Date();
                newDate.setHours(0, 0, 0, 0);
                giltigtFrom = newDate.toLocaleString("sv");
            }
            let giltigtTomDate = new Date(giltigtFrom);
            giltigtTomDate.setHours(0, 0, 0, 0);
            if (this.kontrakt.affarsform === VirkeskopAffarsformer.Leveransvirke) {
                giltigtTomDate = this.getDateLastDayOfNextJuly(giltigtTomDate);
            } else {
                giltigtTomDate.setFullYear(giltigtTomDate.getFullYear() + 5);
            }

            const giltigtTom = giltigtTomDate.toLocaleString("sv");

            this.form.controls.kontraktGiltigtFrom.setValue(giltigtFrom);
            this.form.controls.kontraktGiltigtTom.setValue(giltigtTom);
            this.setKontraktGiltighetChanged();
        }

        this.kontraktMediatorService.statusChanged$
            .next(this.kontrakt.status);
    }

    setKontraktGiltighetChanged(): void {
        this.kontraktMediatorService.kontraktGiltigtChanged$.next(
            this.kontrakt.giltighet);
    }

    setAnbudGiltighetChanged(): void {
        const anbudGiltigtPeriod = new Period();
        anbudGiltigtPeriod.periodFrom = new Date(this.form.controls.anbudGiltighetFrom.value);
        anbudGiltigtPeriod.periodTom = new Date(this.form.controls.anbudGiltighetTom.value);

        this.kontraktMediatorService.anbudGiltigtChanged$.next(anbudGiltigtPeriod);
    }

    getKontraktsStatusar(): string[] {
        if (this.kontrakt.isKontraktMakulerat) {
            return [VirkeskopKontraktstatus.Makulerat];
        }
        return [
            VirkeskopKontraktstatus.Anbud,
            VirkeskopKontraktstatus.Kontrakt,
            VirkeskopKontraktstatus.EjKopt,
            VirkeskopKontraktstatus.Vilande
        ];
    }

    getViolPillboxText() {
        return `Viol ${this.kontrakt.viol3 ? "3" : "2"}`;
    }

    protected disableSkordarmatning(): boolean {
        return (!ObjectUtils.isNullOrUndefined(this.kontrakt?.skickadTillVacs) || !this.angeSkordarmatningIsActive);
    }

    protected get leverantorsOrganisationExternKodControl(): FormControl {
        return this.form.controls.leverantorsOrganisationExternKod as FormControl;
    }

    protected get showVacsStatus() {
        return this.kontrakt.vacsKontraktstatus >= 0 && !this.isSkogsvardFPVKontrakt;
    }

    protected get limitedAccess() {
        return !this.canEdit() && !this.kontrakt.isSkogsvardFPVKontrakt;
    }

    protected get kopplatKontraktLabel(): string {
        return this.kontrakt.kontraktTyp === VirkeskopKontraktstyp.Grot ? "Relaterad rundvirke" : "Relaterad grot";
    }

    protected get isDirty(): boolean {
        return this.kontraktMediatorService.isDirty();
    }

    protected get isGrotuttagforeslaget(): boolean {
        return this.kontraktMediatorService.isGrotuttagforeslaget();
    }

    protected get canUpdateFromRundvirke() {
        return this.kopplingar?.some(x => x.koppladVKKontraktTyp === VirkeskopKontraktstyp.Rundvirke) && this.kontrakt.kontraktTyp === VirkeskopKontraktstyp.Grot;
    }

    protected updateFromRundvirke() {
        if (!this.isDirty) {
            this.kontraktMediatorService.updateGrotFromRundvirke(this.kontrakt.guid);
        }
    }

    protected skapaGrot(): void {
        if (this.isGrotuttagForeslaget && !this.isDirty) {
            this.kontraktMediatorService.skapaGrotFromKontrakt(this.kontrakt.guid);
        }
    }

    protected get showGrotPillbox(): boolean {
        return (this.hasGrotKoppling || this.isGrotuttagforeslaget) &&
            !this.isSkogsvardFPVKontrakt && !this.isSkogsvardskontrakt;
    }

    protected get hasGrotKoppling(): boolean {
        return this.grotKopplingar?.some(x => x.koppladVKKontraktId !== this.kontrakt.id) &&
            !this.kontrakt.isGrot;
    }

    protected get grotKopplingar(): IVKKopplingDTO[] {
        return this.kopplingar?.filter(x => x.koppladVKKontraktTyp === VirkeskopKontraktstyp.Grot);
    }

    protected gotoKopplatKontrakt(koppling: IVKKopplingDTO): void {
        if (this.hasGrotKoppling) {
            this.router.navigate([koppling.koppladVKAvtalId], {
                queryParams: {
                    kontraktId: koppling.koppladVKKontraktId
                },
                relativeTo: this.activatedRoute.parent
            });
        }
    }

    private onHeaderUpdated() {
        this.entity().name = this.kontrakt.kontraktnummer
            ? `${this.kontrakt.kontraktnummer}-${this.kontrakt.benamning}`
            : this.kontrakt.benamning;

        const nettotypmessage = new RuleValidationMessage();
        nettotypmessage.messageId = "nettotyp";
        nettotypmessage.entityGuids = [this.kontrakt.guid];
        this.validationService.updateClientSideValidation(false, nettotypmessage);

        this.kontraktMediatorService.updateKontrakt(this.kontrakt);
    }

    private initKontraktForm() {
        this.formSubscription?.unsubscribe();
        this.form = this.formBinderService.CreateGroup(this.kontrakt);

        if (this.disableSkordarmatning()) {
            this.form.get("skordarmatning").disable();
        }

        if (this.kontrakt.isKontraktMakulerat) {
            this.form.get("status").disable();
        }

        asapScheduler.schedule(() => {
            this.formSubscription = this.form.valueChanges.pipe(
                takeUntil(this.destroy$)
            ).subscribe(() => {
                if (!this.form.dirty) {
                    return;
                }
                this.onHeaderUpdated();
            });
        });
    }

    private getDateLastDayOfNextJuly(inputDate: Date): Date {
        let year = inputDate.getFullYear();
        if (inputDate.getMonth() >= 7) {
            year += 1;
        }

        return new Date(year, 6, 31);
    }
}
